/**
 * md-lite.ts — the one Markdown dialect our stored prose uses (Dignity's scenario
 * sections, the templated code guides): blank-line paragraphs, "- " / "1. "
 * lists, **bold**, a bold-only line as a heading, and "## " headings. Shared by
 * the scenario and code bot renderers so both render prose identically. Moved
 * out of scenario.ts on 2026-09-06 when code guides became Markdown templates.
 */
import { escapeHtml } from './html-renderer';

/** Inline pass: escape, then the one inline mark Dignity uses (**bold**). */
function inline(s: string): string {
  return escapeHtml(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Dignity's text → HTML, verbatim in content. Blank lines separate
 * paragraphs; "- " / "• " / "1. " lines become lists; a line that is only a
 * **bold label** (optionally with a trailing colon) becomes an h3; "## " is
 * honoured if it ever appears. Nothing is dropped or rewritten.
 */
export function mdLite(text: string, opts: { headingLevel?: 2 | 3 } = {}): string {
  const H = `h${opts.headingLevel ?? 3}`;
  const out: string[] = [];
  let list: string[] | null = null;
  let ordered = false;
  const flush = () => {
    if (list) {
      const tag = ordered ? 'ol' : 'ul';
      out.push(`<${tag}>${list.map(li => `<li>${li}</li>`).join('')}</${tag}>`);
      list = null;
    }
  };
  for (const block of text.replace(/\r/g, '').split(/\n{2,}/)) {
    for (const rawLine of block.split('\n')) {
      const line = rawLine.trim();
      if (!line) continue;
      const bullet = line.match(/^[-*•]\s+(.*)$/);
      const num = !bullet ? line.match(/^\d+[.)]\s+(.*)$/) : null;
      if (bullet || num) {
        const isOrdered = !!num;
        if (list && ordered !== isOrdered) flush();
        if (!list) { list = []; ordered = isOrdered; }
        list.push(inline((bullet || num)![1]));
        continue;
      }
      flush();
      const heading = line.match(/^#{1,6}\s+(.*)$/);
      const label = line.match(/^\*\*([^*]+?)\*\*:?$/);
      if (heading) out.push(`<${H}>${inline(heading[1])}</${H}>`);
      // a bold-only line is a run-in label ("**What moves your bill most:**", a Q in a
      // Q&A list) — always one level under the section headings
      else if (label) out.push(`<h3>${escapeHtml(label[1].replace(/:$/, ''))}</h3>`);
      else out.push(`<p>${inline(line)}</p>`);
    }
    flush();
  }
  flush();
  return out.join('\n');
}

